<template>
  <OptimizationDialogHandler
    ref="dialogHandler"
    @hide="(id) => hideOptimization(id)"
    @unhide="(id) => unhideOptimization(id)" />

  <DeleteConfirmDialog
    v-model:visible="hideOptimizationVisible"
    :title="i18n.t('hideOptimization')"
    :loading="hideOptimizationLoading"
    :delete-btn-label="i18n.t('submit')"
    @cancel="hideOptimizationVisible = false"
    @delete="hideOptimization(optimizationToHide)">
    <span>{{ i18n.t('hideOptimizationInfo') }}</span>
  </DeleteConfirmDialog>

  <!-- Header -->
  <!-- Filter is displayed only if there are any optimizations available -->
  <div
    v-if="optimizationsState !== 'empty'"
    class="flex items-center justify-between gap-3 pb-4 pt-2">
    <!-- Search -->
    <div class="flex min-w-0 flex-1 flex-wrap items-center gap-3">
      <SmInput
        v-model="searchQuery"
        style="width: 250px; margin-right: 1px"
        size="small"
        outline
        :label="i18n.t('searchPerson')" />
      <SmDropdown
        :close-on-click-inside="false"
        dropdown-class="w-[30rem] max-w-7xl">
        <template #trigger>
          <SmButton outline size="small">
            <div class="flex items-center gap-2">
              <Filter :size="18" />
              <p>
                <span class="button-text">{{ i18n.t('filter') }}</span>
                <span v-if="filterCount"> ({{ filterCount }}) </span>
              </p>
            </div>
          </SmButton>
        </template>
        <div class="p-4">
          <OptimizationDetailsFilterDialog
            v-model:filter="optimizationsFilter"
            :software-licenses="props.allSoftwareLicenses"
            :suggested-license-options="suggestedLicenseOptions" />
        </div>
      </SmDropdown>
    </div>
    <div class="flex items-center gap-2">
      <SmButton outline size="small" @click="refetchOptimizations()">
        {{ i18n.t('reloadOptimizations') }}
      </SmButton>
      <SmTooltip :content="i18n.t('download')">
        <SmButton size="small" @click="openExportDialog">
          <Download class="text-white" :size="18" />
          <span class="ml-2">{{ i18n.t('export') }}</span>
        </SmButton>
      </SmTooltip>
    </div>
  </div>
  <EmptyState
    v-else-if="optimizationsState === 'empty'"
    class="h-full"
    :icon="ChartLine"
    :title="i18n.t('views.dashboard.optimizations.optimized')"
    :show-create-btn="false"
    :show-docs-btn="false" />

  <!-- Table -->
  <SmTable
    v-if="optimizationsState !== 'empty'"
    :data="optimizations"
    :columns="columns"
    :default-sorting="{
      by: 'savings',
      asc: true,
    }"
    style="max-height: calc(100% - 55px)"
    key-field="_id"
    :loading="loading"
    @scroll-end-reached="loadMoreOptimizations"
    @cell-click="handleCellClick">
    <!-- Status -->
    <template #status="{ row }">
      <SmTooltip placement="left">
        <span
          class="status-tooltip-trigger"
          :aria-label="getStatusIndicatorLabel(row)">
          <span
            class="indicator-dot shrink-0"
            :class="getStatusIndicatorClass(row)" />
        </span>
        <template #content>
          <div class="max-w-[250px]">
            <p>{{ getStatusIndicatorLabel(row) }}</p>
          </div>
        </template>
      </SmTooltip>
    </template>
    <!-- Reason -->
    <template #reason="{ row }">
      <TextMultiline
        :text="getReason(row.type as OptimizationType, row.data)" />
    </template>
    <!-- Person -->
    <template #person="{ row }">
      <TextMultiline
        v-if="row.references.account_id"
        :text="getPersonName(row)" />
    </template>
    <!-- Assigned license -->
    <template #assignedLicense="{ row }">
      <TextMultiline
        :class="
          getSoftwareLicenses(row) === i18n.t('general.noModel')
            ? 'italic text-muted'
            : ''
        "
        :text="getSoftwareLicenses(row)" />
    </template>
    <!-- Suggested license -->
    <template #header-suggestedLicense>
      <div class="flex gap-2 align-middle">
        {{ i18n.t('license.suggestedLicense') }}
        <SmTooltip>
          <template #content>{{
            i18n.t('license.suggestedLicenseTooltip')
          }}</template>
          <Info :size="16" class="mt-[1px] cursor-help" />
        </SmTooltip>
      </div>
    </template>
    <template #suggestedLicense="{ row }">
      <TextMultiline
        v-if="getSoftwareLicensesToAdd(row).length === 0"
        class="italic text-muted">
        {{ i18n.t('general.noSuggestedModel') }}
      </TextMultiline>
      <TextMultiline
        v-else
        v-for="softwareLicense in getSoftwareLicensesToAdd(row)"
        :text="softwareLicense.name" />
    </template>
    <!-- Savings -->
    <template #savings="{ row }">
      <TextMultiline v-if="!row.deprecated">
        {{ getCorrectedPrice(row.savings * 12) }}
      </TextMultiline>
      <span v-else></span>
    </template>
    <!-- Actions -->
    <template #action="{ row }">
      <SmDropdown trigger="click">
        <SmDropdownItem
          v-if="!row.deprecated"
          :icon="Info"
          @click="openDialog(row)">
          {{ i18n.t('showDetails') }}
        </SmDropdownItem>
        <SmDropdownItem
          v-if="!row.hidden"
          :icon="EyeOff"
          @click="openConfirmDialog(row)">
          {{ i18n.t('views.optimizations.table.actions.hideOptimization') }}
        </SmDropdownItem>
      </SmDropdown>
    </template>

    <template #footer-reason>
      <TextMultiline class="text-muted" :text="optimizationCountString" />
    </template>

    <template #footer-savings>
      <TextMultiline
        class="text-muted"
        :text="getCorrectedPrice(totalSavings)" />
    </template>
  </SmTable>

  <OptimizationExportDialog
    v-model:visibility="exportDialogVisible"
    :application-id="props.application?._id" />
</template>

<script setup lang="ts">
  import { OptimizationType, OptimizationRightsizingData } from '@/client'
  import { Column } from '@/components/sm/SmTable.types'
  import { getReason } from '@/utils/optimizations'
  import {
    OptimizationStore,
    EnrichedOptimizations,
  } from '@/stores/optimization/optimization.store'
  import { getCorrectedPrice } from '@/utils/formatter/price'
  import { useI18n } from 'vue-i18n'
  import { sendToast } from '@/components/sm/SmNotification'
  import { ChartLine, EyeOff, Filter, Info, Download } from 'lucide-vue-next'
  import OptimizationDialogHandler from '@/components/optimizations/OptimizationDialogHandler.vue'
  import { SoftwareLicense, SoftwareOut } from '@/client'
  import { OptimizationFilters } from '@/stores/optimization/optimization.filter'
  import { refetchOptimizations } from '@/utils/optimizations'
  import OptimizationExportDialog from './OptimizationExportDialog.vue'

  interface Props {
    application: SoftwareOut
    allSoftwareLicenses: SoftwareLicense[]
  }

  const i18n = useI18n()
  const props = defineProps<Props>()
  const emit = defineEmits(['scroll-end-reached'])

  const INITIAL_ROW_LIMIT = 300
  const ROW_EXPAND_COUNT = 100

  const optimizationsFilter = ref<OptimizationFilters>({
    application_ids: [],
  })

  const suggestedLicenseNames = ref<string[]>([])

  watch(
    () => props.application?._id,
    (applicationId) => {
      optimizationsFilter.value.application_ids = applicationId
        ? [applicationId]
        : []

      suggestedLicenseNames.value = []
    },
    { immediate: true }
  )

  const {
    data: optimizations,
    updateParameters,
    loading,
  } = OptimizationStore.useOptimizations<EnrichedOptimizations>(
    optimizationsFilter,
    { limit: INITIAL_ROW_LIMIT },
    undefined,
    true
  )

  const {
    count: totalOptimizationsCount,
    updateParameters: updateCountParameters,
  } = OptimizationStore.useOptimizationsCount(optimizationsFilter)

  // Handle filter count
  const filterCount = computed(() => {
    return Object.keys(optimizationsFilter.value).filter((key) => {
      // exclude application_ids since the data is already prefiltered by application
      if (['application_ids'].includes(key)) return false

      const filterKey =
        optimizationsFilter.value[key as keyof OptimizationFilters]

      // check if licenses array is empty
      if (Array.isArray(filterKey)) {
        return filterKey.length > 0
      }
      return (
        filterKey !== undefined && filterKey !== null && filterKey !== false
      )
    }).length
  })

  // Handle filter states
  const filterActive = computed(() => filterCount.value > 0)

  const optimizationsState = computed(() => {
    // Store is still loading?
    if (loading.value) {
      return 'loading'
    }

    // is filter active and no match?
    if (filterActive.value && optimizations.value.length === 0) {
      return 'noMatch'
    }
    // no optimizations at all?
    if (!optimizations.value || optimizations.value.length === 0) {
      return 'empty'
    }

    // otherwise show all or filtered data
    return 'data'
  })

  const rowLimit = ref(INITIAL_ROW_LIMIT)

  async function loadMoreOptimizations() {
    rowLimit.value += ROW_EXPAND_COUNT
    updateParameters(optimizationsFilter.value, { limit: rowLimit.value })
  }

  const searchQuery = ref<string>('')
  const exportDialogVisible = ref<boolean>(false)

  function setFilter(newFilter: Partial<OptimizationFilters>) {
    optimizationsFilter.value = {
      ...optimizationsFilter.value,
      ...newFilter,
    }
    rowLimit.value = INITIAL_ROW_LIMIT // reset pagination
    updateParameters(optimizationsFilter.value, { limit: rowLimit.value })
    updateCountParameters(optimizationsFilter.value)
  }

  function openExportDialog() {
    exportDialogVisible.value = true
  }

  watch(searchQuery, (val) => {
    if (val) {
      setFilter({ search: val })
    } else {
      setFilter({ search: undefined })
    }
  })

  watch(
    optimizationsFilter,
    (newFilter) => {
      rowLimit.value = INITIAL_ROW_LIMIT
      updateParameters(newFilter, { limit: rowLimit.value })
    },
    { deep: true }
  )

  watch(
    [optimizations, filterActive],
    ([rows, isFilterActive]) => {
      if (isFilterActive) return

      const names = new Set<string>()

      rows.forEach((optimization) => {
        const data = optimization.data as OptimizationRightsizingData
        const licToAdd = data.software_licenses_to_add ?? []

        licToAdd.forEach((lic: any) => {
          if (lic?.name) {
            names.add(lic.name)
          }
        })
      })

      suggestedLicenseNames.value = Array.from(names)
    },
    { immediate: true }
  )

  // ----------------------------------------------------------------------
  // Table Data
  // ----------------------------------------------------------------------

  const optimizationCountString = computed(() => {
    const loaded = optimizations.value.length
    const total = totalOptimizationsCount.value
    if (total > loaded) {
      return i18n.t('optimizationsCountLoaded', { loaded, total })
    }
    return i18n.t('optimizationsCount', { count: loaded })
  })

  const totalSavings = computed(() => {
    return optimizations.value.reduce((sum, optimization) => {
      return sum + optimization.savings * 12
    }, 0)
  })

  const columns = computed<Column<EnrichedOptimizations>[]>(() => [
    {
      key: 'status',
      label: '',
      width: '50px',
      sortable: false,
      loader: true,
    },
    {
      key: 'reason',
      label: i18n.t('reason'),
      width: 2,
      sortable: false,
      loader: true,
    },
    {
      key: 'person',
      label: i18n.t('person'),
      width: 2,
      sortable: false,
      loader: true,
    },
    {
      key: 'assignedLicense',
      label: i18n.t('license.assignedLicense'),
      width: 2,
      sortable: false,
      loader: true,
    },
    {
      key: 'suggestedLicense',
      label: i18n.t('license.suggestedLicense'),
      width: 2,
      sortable: false,
      loader: true,
    },
    {
      key: 'savings',
      label: i18n.t('yearlySavings'),
      width: 1,
      sortable: true,
      loader: true,
    },
    {
      key: 'action',
      label: '',
      width: '50px',
      sortable: false,
    },
  ])

  function handleCellClick(
    _: number,
    columnKey: string,
    row: EnrichedOptimizations
  ) {
    if (columnKey === 'action') return

    openDialog(row)
  }

  // ---------------------------------------------------------------------------
  // Status
  // ---------------------------------------------------------------------------

  function getStatusIndicatorClass(
    optimization: EnrichedOptimizations
  ): string {
    if (optimization.deprecated) {
      return 'status-dot-deprecated'
    }

    if (optimization.hidden) {
      return 'status-dot-hidden'
    }

    return 'status-dot-visible'
  }

  function getStatusIndicatorLabel(
    optimization: EnrichedOptimizations
  ): string {
    if (optimization.deprecated) {
      return i18n.t('deprecated')
    }

    if (optimization.hidden) {
      return i18n.t('hidden')
    }

    return i18n.t('active')
  }

  // ---------------------------------------------------------------------------
  // Person related
  // ---------------------------------------------------------------------------

  function getPersonName(optimization: EnrichedOptimizations): string {
    return (
      optimization.person_name ||
      [optimization.person_first_name, optimization.person_last_name]
        .filter(Boolean)
        .join(' ') ||
      optimization.person_email ||
      ''
    )
  }

  // ---------------------------------------------------------------------------
  // Software licenses
  // ---------------------------------------------------------------------------

  function getSoftwareLicenses(optimization: EnrichedOptimizations): string {
    const licenseIds = Object.keys(optimization.licenses || {})

    if (licenseIds.length === 0) return i18n.t('general.noModel')

    const licenseNames = licenseIds
      .map((id) => {
        const license = props.allSoftwareLicenses.find((l) => l._id === id)
        return license?.name
      })
      .filter(Boolean)

    return licenseNames.length > 0
      ? licenseNames.join(', ')
      : i18n.t('general.noModel')
  }

  function getSoftwareLicensesToAdd(optimization: EnrichedOptimizations) {
    const data = optimization.data as OptimizationRightsizingData
    return data.software_licenses_to_add ?? []
  }

  const suggestedLicenseOptions = computed(() =>
    suggestedLicenseNames.value
      .map((name) => ({
        label: name,
        value: name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  )

  // ---------------------------------------------------------------------------
  // Handle Dialogs
  // ---------------------------------------------------------------------------

  function openDialog(optimization: EnrichedOptimizations) {
    if (optimization.references.account_id) {
      dialogHandler.value?.showDialog(optimization, optimization.person_id)
    }
  }

  // Optimization Handler
  const dialogHandler = ref<typeof OptimizationDialogHandler>()

  // Hide optimization
  const hideOptimizationVisible = ref<boolean>(false)
  const optimizationToHide = ref<string>('')
  const hideOptimizationLoading = ref<boolean>(false)

  function openConfirmDialog(optimization: EnrichedOptimizations) {
    hideOptimizationVisible.value = true
    optimizationToHide.value = optimization._id
    hideOptimizationLoading.value = false
  }

  async function hideOptimization(id: string) {
    hideOptimizationLoading.value = true
    await OptimizationStore.hideOptimization(id).then(() => {
      sendToast(i18n.t('hideOptimizationSuccess'), undefined, 'success')
      hideOptimizationLoading.value = false
      hideOptimizationVisible.value = false
      dialogHandler.value?.closeDialog()
    })
  }

  async function unhideOptimization(id: string) {
    await OptimizationStore.unhideOptimization(id).then(() => {
      sendToast(
        i18n.t('notifications.optimizationsView.showOptimizationSuccess.title'),
        undefined,
        'success'
      )
      dialogHandler.value?.closeDialog()
    })
  }

  // ensure reset
  watch(
    () => hideOptimizationVisible.value,
    (newVal) => {
      if (!newVal) {
        hideOptimizationLoading.value = false
        optimizationToHide.value = ''
      }
    }
  )
</script>

<style scoped>
  .indicator-dot {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .status-tooltip-trigger {
    display: flex;
    width: 100%;
    height: 100%;
    min-height: 24px;
    align-items: center;
    padding-left: 0.75rem;
  }

  .status-dot-visible {
    background-color: var(--sm-green);
  }

  .status-dot-hidden {
    background-color: var(--sm-contrast-muted);
  }

  .status-dot-deprecated {
    background-color: var(--sm-yellow);
  }
</style>
